import { RefObject, useMemo, memo, useState, useEffect } from "react";
import {
  ISharedConversationWatcher,
  TypeConversationMessage,
  ISharedFullConversationReducer,
  IPreviewTab,
  DirectionEnum,
  ChannelEnum,
  IChatSessionStatus,
  IChatConfig,
  TConversationChatSession,
} from "atlas-shared";
import "./tab.mini.conversation.list.scss";
import { useTranslation } from "react-i18next";
import { IAuth } from "@Store";
import { AppSpinner, Avatar, ConversationDispositionNote } from "@Components";
import { THighlightedMessage, useChatSession, useConversationScroll, useUsers } from "@Hooks";
import { TabMiniConversationMessage } from "./tab.mini.conversation.message";
import { useConversationTextMessages } from "@Components/conversation/conversation.messages/useConversationTextMessages";
import { getCreatedDate, tryCatch } from "@Utils";
import { getChatSessionsByConversationId } from "@Api";
import { useAsync } from "@Hooks";
import { ConversationMessageChatSessionMini } from "@Components/conversation/conversation.messages/conversation.message/conversation.chat_session/conversation.chat_session.mini";
import { Button, Input } from "antd";

interface IProps {
  conversationId: number;
  source: ISharedConversationWatcher["source"];
  className?: string;
  conversation: ISharedFullConversationReducer;
  auth: IAuth;
  highlightedMessage: THighlightedMessage;
  scrollRef: RefObject<HTMLDivElement>;
  tab?: IPreviewTab;
}
export const TabMiniConversationList = memo(
  ({ className, conversation, auth, highlightedMessage, scrollRef, tab }: IProps) => {
    const { t } = useTranslation();
    const users = useUsers();
    const { textMessages } = useConversationTextMessages(conversation.id);
    const { data: chatSessions } = useAsync(() => getChatSessionsByConversationId(conversation.id));
    const {
      connectChatSession,
      messages: chatMessages,
      sendMessage,
      sessionStatus,
      handleTypingStart,
      handleTypingStop,
      userIsTyping,
    } = useChatSession(conversation.id, conversation);
    const [message, setMessage] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const conversationMessages = conversation?.messages || [];
    const combinedConversationMessages = useMemo(
      () => [
        ...conversationMessages,
        ...(textMessages?.map(
          (textMsg) =>
            ({
              ...textMsg,
              channel: ChannelEnum.Sms,
              createdAt: textMsg.created_at,
              id: textMsg.id,
              direction: textMsg.direction === "I" ? DirectionEnum.InBound : DirectionEnum.Outbound,
              organization_id: conversation?.organization_id,
              conversation_id: conversation?.id,
            } as TypeConversationMessage)
        ) || []),
        ...(chatSessions ?? []),
      ],
      [
        conversationMessages,
        textMessages,
        chatSessions,
        conversation?.organization_id,
        conversation?.id,
      ]
    );

    const getSortDate = (item: any) => {
      if (item.chatSessionMessages && item.chatSessionMessages.length > 0) {
        return getCreatedDate(item.chatSessionMessages[0])?.getTime() ?? 0;
      }
      return getCreatedDate(item)?.getTime() ?? 0;
    };

    const sortedConversationMessages = combinedConversationMessages.sort(
      (a, b) => getSortDate(b) - getSortDate(a)
    );

    const activeChatSession = sortedConversationMessages.find(
      (item: any) => item.chatSessionMessages && item.status === IChatSessionStatus.ACTIVE
    ) as TConversationChatSession | undefined;

    const isSessionActive = sessionStatus
      ? sessionStatus === IChatSessionStatus.ACTIVE
      : activeChatSession?.status === IChatSessionStatus.ACTIVE;

    const { scrollToBottom, isAtBottom } = useConversationScroll(scrollRef, {
      hasActiveChatSession: !!activeChatSession,
      chatMessages: chatMessages || [],
      isInitialLoad,
    });

    const sendChatMessage = async (e?: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Only prevent default and send if it's Enter without Shift
      if (e && e.shiftKey) {
        return; // Allow default behavior for Shift+Enter (line break)
      }

      e?.preventDefault();
      if (isSending || !message.trim() || !activeChatSession || !isSessionActive) return;

      const messageToSend = message.trim();
      setMessage("");
      handleTypingStop();
      setIsSending(true);

      const { data, error } = await tryCatch(
        sendMessage(
          messageToSend,
          { id: activeChatSession.chatConfigId } as IChatConfig,
          activeChatSession
        )
      );
      if (error) {
        console.error(error);
      }
      setIsSending(false);
    };

    useEffect(() => {
      if (
        activeChatSession &&
        sessionStatus !== IChatSessionStatus.ENDED &&
        sessionStatus !== IChatSessionStatus.TIMED_OUT
      ) {
        connectChatSession(activeChatSession.chatConfigId, activeChatSession.sessionId);
      }
    }, [activeChatSession, sessionStatus, connectChatSession]);

    useEffect(() => {
      if (conversation && isInitialLoad) {
        setTimeout(() => {
          setIsInitialLoad(false);
        }, 200);
      }
    }, [conversation, isInitialLoad]);

    if (!conversation) {
      return <AppSpinner />;
    }

    const onGoingChatSession =
      chatSessions &&
      chatSessions.length > 0 &&
      chatSessions[0].chatSessionMessages &&
      isSessionActive;

    return (
      <div className={"tab-mini-conversation-list-view-container " + className}>
        {activeChatSession && !isAtBottom && (
          <Button
            type="primary"
            shape="circle"
            size="small"
            className="scroll-to-bottom-btn"
            onClick={() => scrollToBottom(true)}
          >
            ↓
          </Button>
        )}
        <div
          className={"body"}
          ref={scrollRef}
          style={{
            // Reserve space for input if there is an ongoing chat session
            marginBottom: onGoingChatSession ? "65px" : undefined,
            position: "relative",
          }}
        >
          {conversation.disposition_id && (
            <div className="row-right conversation-message-row">
              <Avatar
                size={20}
                user={conversation.user_id ? users.dict[conversation.user_id] : undefined}
              />
              <ConversationDispositionNote conversation={conversation} mini={true} />
            </div>
          )}
          {sortedConversationMessages.map((item: any) => {
            if (item.chatSessionMessages) {
              return (
                <ConversationMessageChatSessionMini
                  key={`chat-session-${item.id}`}
                  chatMessages={chatMessages}
                  userIsTyping={userIsTyping}
                />
              );
            }
            return (
              <TabMiniConversationMessage
                key={`${item.channel}${item.id}`}
                t={t}
                auth={auth}
                message={item}
                conversation={conversation}
                users={users}
                highlightedMessage={highlightedMessage}
                tab={tab}
              />
            );
          })}
        </div>

        {activeChatSession && isSessionActive && (
          <div className="tab-mini-chat-input-container">
            <Input.TextArea
              className="tab-mini-chat-input"
              value={message}
              onChange={(e) => {
                const newValue = e.target.value;
                setMessage(newValue);
                if (newValue && newValue.trim()) {
                  handleTypingStart();
                }
              }}
              onBlur={() => {
                handleTypingStop();
              }}
              placeholder={t("CHAT_MESSAGE_PLACEHOLDER")}
              onPressEnter={(e) => sendChatMessage(e)}
            />
            <Button
              size="small"
              type="primary"
              className="tab-mini-chat-send-button"
              onClick={() => sendChatMessage()}
              disabled={isSending || !message.trim()}
            >
              {t("SEND")}
            </Button>
          </div>
        )}
      </div>
    );
  }
);
